// Shared visual tokens for the left-hand panels (vessel filters + voyage
// planner) so their cards, text colours and inputs stay in sync. Colours come
// from theme/tokens.js — the single source shared with the MUI theme.

import {
  WW_BLUE,
  WW_BLUE_DARK,
  PANEL_TEXT_MUTED,
  PANEL_TEXT_FAINT,
  PANEL_CARD_BG,
  PANEL_CARD_BORDER,
  PANEL_DIVIDER,
} from "./tokens"

export const PANEL_TEXT = WW_BLUE
export { PANEL_TEXT_MUTED, PANEL_TEXT_FAINT }

// Subtly highlighted near-white sub-card, matching the vessel panel's cards:
// #fafbfc fill, blue-tinted border, generous radius.
export const PANEL_CARD_SX = {
  p: 1.25,
  borderRadius: "14px",
  bgcolor: PANEL_CARD_BG,
  border: `1px solid ${PANEL_CARD_BORDER}`,
}

// Panel typography — one definition for both the voyage planner and the RNW
// analyser so the sizes can't drift apart again.
// Panel header (title + subtitle under it).
export const PANEL_TITLE_SX = {
  fontSize: "16px",
  fontWeight: 700,
  color: WW_BLUE,
  letterSpacing: "0.01em",
}
export const PANEL_SUBTITLE_SX = { fontSize: "12px", color: PANEL_TEXT_MUTED }
// Sub-card heading ("Departure & Speed", "Display Metrics", …).
export const CARD_TITLE_SX = { fontSize: "12px", fontWeight: 700, color: WW_BLUE }
// Helle Subcard-Variante (Weather-Panel): zurückgenommen, damit aktive Zeilen tragen.
export const PANEL_SUBCARD_SX = {
  border: "1px solid #e9eff4",
  borderRadius: "10px",
  bgcolor: "#fafcfd",
  p: "10px 8px",
}
// Full-bleed divider under the panel header (negative mx spans the p:1.5
// panel padding). Use with MUI <Divider>.
export const PANEL_HEADER_DIVIDER_SX = {
  mx: -1.5,
  mb: 1.25,
  borderColor: PANEL_DIVIDER,
}

// Row label + small grey description line beneath it.
export const ROW_LABEL_SX = { fontSize: "12px", fontWeight: 600, color: WW_BLUE }
export const ROW_DESCRIPTION_SX = { fontSize: "10.5px", color: PANEL_TEXT_FAINT }

// Pill buttons shared by the Fleet Explorer header (Back/Table) and the
// vessel-spec editors (Edit/Save/Cancel), so their geometry can't drift.
// Hover stays MUI default (shadow raise, constant color) like Import route.
export const PANEL_BUTTON_SX = {
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "12px",
  px: 1.25,
  py: 0.5,
  flexShrink: 0,
  backgroundColor: WW_BLUE,
}
export const PANEL_BUTTON_OUTLINED_SX = {
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "12px",
  px: 1.25,
  py: 0.5,
  flexShrink: 0,
  color: WW_BLUE,
  borderColor: "rgba(0, 79, 125, 0.35)",
}

// Slim panel scrollbar (scroll bodies of the left panels).
export const PANEL_SCROLLBAR_SX = {
  "&::-webkit-scrollbar": { width: 6, height: 6 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 79, 125, 0.25)",
    borderRadius: 3,
  },
}

// Font size shared by every editable input across the panels (vessel search,
// route endpoints, departure picker, base speed) so they stay aligned.
export const PLAN_INPUT_FONT_SIZE = "13px"

// Shared outlined-field styling for the plain panel TextFields (route
// endpoints + base speed): same radius, background and input font size.
export const PLAN_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    minHeight: 36,
    borderRadius: "12px",
    bgcolor: "rgba(255,255,255,0.74)",
  },
  "& .MuiInputBase-input": {
    fontSize: PLAN_INPUT_FONT_SIZE,
    py: 0.4,
    minWidth: "140px",
  },
}

// slotProps for the panel DateTimePicker so its popup matches the panel:
// compact, rounded, #004f7d accents instead of the default MUI light blue.
export const PLAN_DATEPICKER_SLOTPROPS = {
  popper: { sx: { zIndex: 1400 } },
  desktopPaper: {
    sx: {
      borderRadius: "14px",
      border: "1px solid rgba(0, 79, 125, 0.10)",
      boxShadow: "0 8px 28px rgba(18, 52, 86, 0.18)",
      color: WW_BLUE,
      "& .MuiPickersCalendarHeader-label": {
        fontSize: "13px",
        fontWeight: 700,
        color: WW_BLUE,
      },
      "& .MuiPickersCalendarHeader-switchViewButton, & .MuiPickersArrowSwitcher-button":
        { color: WW_BLUE },
      "& .MuiDayCalendar-weekDayLabel": {
        fontSize: "11px",
        color: PANEL_TEXT_FAINT,
      },
      "& .MuiPickerDay-root": {
        fontSize: "12px",
        color: WW_BLUE,
        "&:hover": { backgroundColor: "rgba(0, 79, 125, 0.08)" },
        "&.MuiPickerDay-today:not(.Mui-selected)": {
          borderColor: "rgba(0, 79, 125, 0.4)",
        },
      },
      "& .MuiPickerDay-root.Mui-selected": {
        backgroundColor: `${WW_BLUE} !important`,
        color: "#fff !important",
      },
      "& .MuiMultiSectionDigitalClock-root, & .MuiDigitalClock-root": {
        "& .MuiMenuItem-root": { fontSize: "12px", color: WW_BLUE },
        "& .Mui-selected": {
          backgroundColor: `${WW_BLUE} !important`,
          color: "#fff",
        },
      },
      "& .MuiDialogActions-root": {
        gap: "6px",
        padding: "6px 12px 10px",
        "& .MuiButton-root": {
          minWidth: 0,
          px: 1.5,
          py: 0.4,
          fontSize: "12px",
          fontWeight: 600,
          textTransform: "none",
          borderRadius: "999px",
          color: WW_BLUE,
          "&:hover": { backgroundColor: "rgba(0, 79, 125, 0.08)" },
        },
        "& .MuiButton-root:last-of-type": {
          color: "#fff",
          backgroundColor: WW_BLUE,
          "&:hover": { backgroundColor: WW_BLUE_DARK },
        },
      },
    },
  },
}

// Small field label above panel inputs (Options-step tabs, DecimalField).
export const FIELD_LABEL_SX = {
  fontSize: "9.5px",
  color: PANEL_TEXT_FAINT,
  mb: 0.25,
}
